import {
    APP_ID,
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    LOCALE_ID,
    PLATFORM_ID,
    provideZonelessChangeDetection,
} from '@angular/core';
import {
    provideRouter,
    Router,
    withComponentInputBinding,
    withInMemoryScrolling,
    withPreloading,
    withRouterConfig,
    withViewTransitions,
} from '@angular/router';
import { version } from '../../package.json';

import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import '@angular/common/locales/global/en';
import '@angular/common/locales/global/ro';
// SSR disabled - hydration not needed
// import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withI18nSupport } from '@angular/platform-browser';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withI18nSupport } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { IconDefinition } from '@ant-design/icons-angular';
import {
    CheckCircleFill,
    CloseOutline,
    DeleteOutline,
    LoadingOutline,
    MailOutline,
    MenuOutline,
    MinusOutline,
    PlusOutline,
    SearchOutline,
    UserOutline
} from '@ant-design/icons-angular/icons';
import {
    MissingTranslationHandler,
    MissingTranslationHandlerParams,
    provideMissingTranslationHandler,
    provideTranslateService,
    TranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { en_US, NZ_I18N, ro_RO } from 'ng-zorro-antd/i18n';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { environment } from 'src/environments/environment';
import { appInitializerFactory } from './app.component';
import { routes } from './app.routes';
import { ENGLISH_US, SupportedLocales } from './constants';
import { apiKeyInterceptor, duplicateRequestInterceptor, errorNotificationInterceptor, jwtInterceptor } from './interceptors';
import {
    CartItemModelResponseDto,
    NotificationModelResponseDto,
    SessionModelResponseDto,
    SuborderModelResponseDto,
    WaiterCommandModelResponseDto,
} from './models';
import {
    ApiService,
    AuthenticationService,
    CART_ITEMS_WEBSOCKET_SERVICE,
    FingerprintService,
    LocalesService,
    LocalStorageService,
    NOTIFICATIONS_WEBSOCKET_SERVICE,
    SESSIONS_WEBSOCKET_SERVICE,
    SUBORDERS_WEBSOCKET_SERVICE,
    TitleService,
    UserAccountService,
    WAITER_COMMANDS_VENUES_WEBSOCKET_SERVICE,
    WAITER_COMMANDS_WEBSOCKET_SERVICE,
    WebsocketService,
} from './services';
import { FlagBasedPreloadingStrategy } from './shared/strategy';

// Only load essential icons initially to reduce bundle size
const criticalIcons: IconDefinition[] = [
  CheckCircleFill,
  MailOutline,
  MenuOutline,
  CloseOutline,
  LoadingOutline,
  PlusOutline,
  MinusOutline,
  DeleteOutline,
  SearchOutline,
  UserOutline
];

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    if (!environment.production) {
      console.warn(`Missing translation for key: ${params.key}`);
    }
    return params.key;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      routes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withPreloading(FlagBasedPreloadingStrategy),
      withComponentInputBinding(),
      withViewTransitions(),
    ),
    provideHttpClient(
      withInterceptors([apiKeyInterceptor, jwtInterceptor, errorNotificationInterceptor, duplicateRequestInterceptor]),
      withFetch(),
    ),
    provideClientHydration(
      withEventReplay(),
      withI18nSupport(),
      withHttpTransferCacheOptions({ 
        includeHeaders: ['authorization', 'api-key'], 
        includeRequestsWithAuthHeaders: true,
        filter: (req) => {
          // Cache only GET requests and exclude real-time endpoints
          return req.method === 'GET' && !req.url.includes('/realtime');
        }
      }),
    ),
    // Experimental
    provideZonelessChangeDetection(),
    provideTranslateService({
      fallbackLang: ENGLISH_US,
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: `.json?v=${environment.production ? version : Date.now()}`,
      }),
      missingTranslationHandler: provideMissingTranslationHandler(MyMissingTranslationHandler),
    }),
    provideNzIcons(criticalIcons),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TitleService, LocalesService, FingerprintService, LocalStorageService, TranslateService, PLATFORM_ID],
      multi: true,
    },
    { provide: APP_ID, useValue: 'waytr-qr' },
    {
      provide: NZ_I18N,
      useFactory: () => {
        const localId = inject(LOCALE_ID);
        switch (localId) {
          case SupportedLocales.EN_US:
            return en_US;
          /** keep the same with angular.json/i18n/locales configuration **/
          case SupportedLocales.RO_RO:
            return ro_RO;
          default:
            return en_US;
        }
      },
    },
    ApiService,
    // Create multiple instances of WebsocketService for each domain
    {
      provide: CART_ITEMS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<CartItemModelResponseDto>('cart-items', userAccountService, authService, router);
      },
    },
    {
      provide: SUBORDERS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<SuborderModelResponseDto>('suborders', userAccountService, authService, router);
      },
    },
    {
      provide: SESSIONS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<SessionModelResponseDto>('sessions', userAccountService, authService, router);
      },
    },
    {
      provide: NOTIFICATIONS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<NotificationModelResponseDto>('notifications', userAccountService, authService, router);
      },
    },
    {
      provide: WAITER_COMMANDS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<WaiterCommandModelResponseDto>('waiter_commands', userAccountService, authService, router);
      },
    },
    {
      provide: WAITER_COMMANDS_VENUES_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<WaiterCommandModelResponseDto>('waiter_commands_venues', userAccountService, authService, router);
      },
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
